import { openDialog, replaceDialog } from '@patrianna/dialog-manager'
import type { ActionTypeMapFnPayments } from '@patrianna-payments/shared-store/payments/store'
import { removePaymentCard } from '@patrianna-payments/shared-store/payments/store/actions'
import {
  buyShopOfferActionName,
  createOrderResponseCardPaymentErrorHandlerActionName,
  createOrderResponseCardQuickPurchasePaymentErrorHandlerActionName,
  finishedPaymentFlowActionName,
  finishedQuickPurchasePaymentFlowActionName,
  getOffersActionName,
  offerDeclineNotificationHandlerActionName,
  offerDeclineTrustlyHandlerActionName,
  offerPurchaseSuccessActionName,
  openPaymentProvidersDialogActionName,
  paymentErrorsHandlerActionName,
  purchaseResponseListenerByRouterActionName,
  sentryCaptureMessageActionName,
  sentryCaptureExceptionActionName,
  start3dsFlowActionName,
  quickPurchasePaymentErrorsHandlerActionName,
  paymentsOpenDialogActionName,
  paymentsReplaceDialogActionName,
  openRemoveAccountPaymentMethodsDialogActionName,
} from '@patrianna-payments/shared-store/payments/store/middleware-actions'
import { WithdrawMethodType } from '@patrianna-payments/shared-store/redeem/types/WithdrawMethodType'
import type { AnyAction } from '@reduxjs/toolkit'

import {
  handle3dsFlowStart,
  handleBuyShopOfferAction,
  handleCreateOrderResponseCardPaymentErrorHandlerAction,
  handleCreateOrderResponseCardQuickPurchasePaymentErrorHandlerAction,
  handleFinishedPaymentFlowAction,
  handleFinishedQuickPurchasePaymentFlowAction,
  handleOfferDeclineNotificationHandlerAction,
  handleOfferDeclineTrustlyHandlerAction,
  handleOfferPurchaseSuccessAction,
  handleOpenPaymentProvidersDialogAction,
  handleOpenRemoveAccountPaymentMethodsDialogAction,
  handlePaymentErrorsHandlerAction,
  handlePurchaseResponseListenerByRouterAction,
  handleQuickPurchasePaymentErrorsHandlerAction,
} from 'store/middlewares/handlers/payments'
import { handleSentryCaptureException, handleSentryCaptureMessage } from 'store/middlewares/handlers/sentry'
import type { MiddlewareType } from 'store/middlewares/types'
import { getOffers } from 'store/modules/shop/actions'
import type { TypedThunk } from 'store/types'

const actionsMap: Record<string, ActionTypeMapFnPayments<TypedThunk>> = {
  [finishedPaymentFlowActionName]: handleFinishedPaymentFlowAction,
  [finishedQuickPurchasePaymentFlowActionName]: handleFinishedQuickPurchasePaymentFlowAction,
  [offerPurchaseSuccessActionName]: handleOfferPurchaseSuccessAction,
  [getOffersActionName]: getOffers,
  [paymentErrorsHandlerActionName]: handlePaymentErrorsHandlerAction,
  [quickPurchasePaymentErrorsHandlerActionName]: handleQuickPurchasePaymentErrorsHandlerAction,
  [purchaseResponseListenerByRouterActionName]: handlePurchaseResponseListenerByRouterAction,
  [offerDeclineTrustlyHandlerActionName]: handleOfferDeclineTrustlyHandlerAction,
  [offerDeclineNotificationHandlerActionName]: handleOfferDeclineNotificationHandlerAction,
  [buyShopOfferActionName]: handleBuyShopOfferAction,
  [createOrderResponseCardPaymentErrorHandlerActionName]: handleCreateOrderResponseCardPaymentErrorHandlerAction,
  [openPaymentProvidersDialogActionName]: handleOpenPaymentProvidersDialogAction,
  [sentryCaptureMessageActionName]: handleSentryCaptureMessage,
  [createOrderResponseCardQuickPurchasePaymentErrorHandlerActionName]:
    handleCreateOrderResponseCardQuickPurchasePaymentErrorHandlerAction,
  [sentryCaptureExceptionActionName]: handleSentryCaptureException,
  [start3dsFlowActionName]: handle3dsFlowStart,
  [openRemoveAccountPaymentMethodsDialogActionName]: handleOpenRemoveAccountPaymentMethodsDialogAction,
}

const dialogsActionsMap = {
  [paymentsOpenDialogActionName]: openDialog,
  [paymentsReplaceDialogActionName]: replaceDialog,
}

export const paymentsMiddleware: MiddlewareType =
  ({ dispatch }) =>
  (next) =>
  (action: AnyAction) => {
    if (action.type in actionsMap) {
      dispatch(actionsMap[action.type](action?.payload))
    }

    if (action.type in dialogsActionsMap) {
      const { modalName: dialogName, ...params } = action.payload

      dialogsActionsMap[action.type]({ dialogName, ...params })
    }

    if (action.type === removePaymentCard.type && action.payload?.provider === WithdrawMethodType.TRUSTLY) {
      return null
    }

    next(action)
  }
